import React, { ReactNode } from 'react';
import { IconButton as MuiIconButton } from '@mui/material';

/**
 * @typedef {('small' | 'medium')} ButtonIconSizes - Size options for the button.
 * @typedef {('default' | 'primary')} IconButtonTypes - Type options for the button.
 */
type ButtonIconSizes = 'small' | 'medium';
type IconButtonTypes = 'default' | 'primary';

interface IconButtonProps {
  children: ReactNode;
  buttonSize?: ButtonIconSizes;
  iconSize?: ButtonIconSizes;
  className?: string;
  type?: IconButtonTypes;
  disabled?: boolean;
  extraStyles?: any;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement> | undefined;
  id?: string;
}

/**
 * IconButton Component
 *
 * A customizable IconButton component built using Material-UI's IconButton.
 *
 * IconButton Component has following properties.
 * @param {ReactNode} children -  The content of the IconButton.
 * @param {ButtonIconSizes} [buttonSize='medium'] - Size of the IconButton.
 * @param {ButtonIconSizes} [iconSize='medium'] - Size of the icon within the IconButton.
 * @param {string} [className=''] - Additional CSS class names.
 * @param {IconButtonTypes} [type='default'] - Type of the IconButton.
 * @param {boolean} [disabled=false] - Whether the IconButton is disabled.
 * @param {function} [onClick] - Click event handler for the IconButton.
 * @param {function} [onKeyDown] - Keydown event handler for the IconButton.
 * @param {Object} [extraStyles={}] - Additional CSS styles to apply to the IconButton.
 * @return {JSX.Element} The rendered IconButton component.
 *
 * @component
 * @example
 * import IconButton from './IconButton';
 * import ChevronRightIcon from '@mui/icons-material/ChevronRight';
 *
 * // Default IconButton
 *   <IconButton buttonSize="medium" iconSize="medium">
 *       <ChevronRightIcon />
 *   </IconButton>
 *
 * // primary IconButton
 *   <IconButton type="primary" buttonSize="medium" iconSize="small">
 *       <ChevronRightIcon />
 *   </IconButton>
 */
export const IconButton = ({
  children,
  buttonSize = 'medium',
  iconSize = 'medium',
  className = '',
  type = 'default',
  disabled = false,
  onClick = undefined,
  onKeyDown = undefined,
  extraStyles = {},
  id = '',
}: IconButtonProps) => {
  const styleOptions = {
    ...extraStyles,
  };

  const modifiedClassName = `custom-icon-button custom-icon-button-${type} button-${buttonSize}-icon-${iconSize} ${className}`;
  return (
    <MuiIconButton
      className={modifiedClassName}
      disabled={disabled}
      onClick={onClick}
      onKeyDown={onKeyDown}
      disableFocusRipple
      sx={styleOptions}
      id={id}
    >
      {children}
    </MuiIconButton>
  );
};
